import type { PersistedDefaultTabProps, TabProps } from '@talos/kyoko';
import { useMemo } from 'react';
import type { NavigationItem } from '../../../../tokens/navigationPaths';
import type { LayoutSection, LayoutSpec } from '../../../../types/LayoutConfig';
import { getMobileLayoutPanelTabRoute } from '../../mobileLayoutRoutes';
import { getTabsStateFromPanelConfig, type PanelConfigWithTabs } from './navRoutes';
import { resolveVisiblePanelTabs } from './visiblePanelTabs';

export interface PanelTabNavItemsResult {
  items: NavigationItem[];
  /** id of the tab that should be considered selected (persisted selectedIndex when present, otherwise the first default tab). */
  selectedTabId: string | undefined;
}

/**
 * Resolves the tabs that should appear under a blotter panel in the mobile nav.
 *
 * Priority order:
 *  1. Persisted tabs from `panelConfig.tabs.items`, when the user has interacted with the panel.
 *  2. The blotter-specific default tabs (passed in by the caller).
 */
export function usePanelTabNavItems<T extends TabProps>({
  sectionId,
  layout,
  panelId,
  panelConfig,
  defaultTabs,
  getTabIndicatorCount,
  isTabIncluded,
  mapTab,
}: {
  sectionId: LayoutSection;
  layout: LayoutSpec | undefined;
  panelId: string;
  panelConfig: PanelConfigWithTabs<T> | undefined;
  defaultTabs: PersistedDefaultTabProps<T>[];
  getTabIndicatorCount?: (
    tab: T | PersistedDefaultTabProps<T>,
    tabId: string,
    sourceTabs: (T | PersistedDefaultTabProps<T>)[]
  ) => number | undefined;
  isTabIncluded?: (tab: T | PersistedDefaultTabProps<T>) => boolean;
  mapTab?: (tab: T | PersistedDefaultTabProps<T>) => T | PersistedDefaultTabProps<T>;
}): PanelTabNavItemsResult {
  return useMemo(() => {
    if (layout == null) {
      return { items: [], selectedTabId: undefined };
    }

    const tabsState = getTabsStateFromPanelConfig(panelConfig);
    const { sourceTabs, selectedTabId } = resolveVisiblePanelTabs({
      persistedItems: tabsState?.items ?? [],
      persistedSelectedIndex: tabsState?.selectedIndex,
      defaultTabs,
      isTabIncluded,
      mapTab,
    });
    if (sourceTabs.length === 0) {
      return { items: [], selectedTabId: undefined };
    }

    const items: NavigationItem[] = sourceTabs.map((tab, index) => {
      const tabId = tab.id ?? `tab-${index}`;
      return {
        id: tabId,
        label: tab.label ?? `Tab ${index + 1}`,
        indicatorCount: getTabIndicatorCount?.(tab, tabId, sourceTabs),
        path: getMobileLayoutPanelTabRoute(sectionId, layout, panelId, tabId),
        isEditable: tab.editable === true,
        isRemovable: tab.closable === true && sourceTabs.length > 1,
      };
    });

    return { items, selectedTabId };
  }, [sectionId, layout, panelId, panelConfig, defaultTabs, getTabIndicatorCount, isTabIncluded, mapTab]);
}
